import { isDeliveryBlank } from '../delivery-rules';
import type { QuotedPrice } from '../../../pricing/catalog-suggestions';
import type { TierEntryMode } from '../../../pricing/tier-pricing';
import type {
	CreateOrderQuoteOfferBatchAdjustmentInput,
	CreateOrderQuoteOfferBatchSampleInput,
} from '@codezo/graphql';
import { OrderShippingMethod } from '@codezo/graphql';

/* ── Step type ── */

export type Step = 'info' | 'products' | 'pricing' | 'delivery';

/* ── Quote item from parent ── */

export interface QuoteItem {
	id: string;
	productId: string;
	productName?: string | null;
	productSku?: string | null;
	/* Frozen snapshot of the catalogue images: url + isPrimary only, and the url
	   is signed with a one-hour expiry. Render it; never persist it or key off it. */
	productImages?: Array<{ url?: string | null; isPrimary?: boolean | null }> | null;
	quantities: Array<{ id: string; quantity: number }>;
	/** Item-level deadline; falls back to the quote deadline for feasibility checks. */
	deadline?: string | null;
}

/* ── Pricing types ── */

export type DeliveryValues = {
	shippingMethod: OrderShippingMethod;
	/** What the client pays for this method — see `DeliveryDraft` in delivery-rules. */
	price: string;
	deliveryDaysMin: string;
	deliveryDaysMax: string;
};

export type PricingValues = {
	/** `baseUnitPrice` — what we pay. Still named `price` for the delivery rules. */
	price: string;
	minDays: string;
	maxDays: string;
	/** `unitSalePrice` — what the client pays. The primary input. */
	salePrice: string;
	/** `markupPercentage` — the secondary way in, kept in step with the price. */
	markup: string;
	/** Which of the two the admin typed. Only that one is sent. */
	entryMode: TierEntryMode;
	/** The catalogue price these figures came from — quantity and supplier
	 *  together, since a quantity alone can name two rows. */
	sourceQuantity?: number | null;
	sourceVetterEntityId?: string | null;
	/** Set once the admin acts — until then a suggestion shows as a ghost. */
	touched?: boolean;
	/**
	 * Filled by the exact-match rule rather than by a person. Counts as touched
	 * for the ghost, but may be re-resolved when the supplier changes — an
	 * admin's own figures never are.
	 */
	autoApplied?: boolean;
	/**
	 * Set when the figures came from a supplier's price request (a VETTER
	 * suggestion). Their shipping is their COST, but it prefills the delivery
	 * price the CLIENT pays — kept here so the delivery step can say so until
	 * the admin has set that price themselves.
	 */
	vetterQuote?: VetterQuoteOrigin | null;
	/** What was really quoted when the cost was converted into the form's currency. */
	quoted?: QuotedPrice | null;
	deliveries: DeliveryValues[];
};

export type VetterQuoteOrigin = {
	vetterName: string;
	currency: string;
	/** The supplier's shipping cost per method, for the whole tier. */
	shipping: Partial<Record<OrderShippingMethod, number>>;
	/**
	 * What was written into each delivery price — `shipping` converted into the
	 * form's currency. The "still their price" warning compares against this.
	 */
	prefilled: Partial<Record<OrderShippingMethod, number>>;
};

export const emptyPricing: PricingValues = {
	price: '',
	minDays: '',
	maxDays: '',
	salePrice: '',
	markup: '',
	entryMode: 'sale',
	deliveries: [],
};

/** True when a tier has any pricing data worth copying to another tier. */
export function hasPricingData(v: PricingValues | undefined): boolean {
	if (!v) return false;
	// A method entry is created as soon as any of its fields is touched and is
	// never removed, so an emptied-out one lingers. Counting it would send an
	// untouched tier to the API priced at zero.
	const hasDelivery = v.deliveries.some((d) => !isDeliveryBlank(d));
	return Boolean(v.price || v.minDays || v.maxDays || v.salePrice || v.markup || hasDelivery);
}

/** Deep-clone a tier's pricing values (deliveries copied by value). */
export function clonePricing(v: PricingValues): PricingValues {
	return { ...v, deliveries: v.deliveries.map((d) => ({ ...d })) };
}

/* ── Re-export input types for convenience ── */

export type AdjustmentInput = CreateOrderQuoteOfferBatchAdjustmentInput;
export type SampleInput = CreateOrderQuoteOfferBatchSampleInput;

/* ── Helper: get primary image URL from a QuoteItem ── */

/** The primary image object (carries `crop` for display). */
export function primaryImageObject(item: QuoteItem) {
	const imgs = item.productImages;
	if (!imgs?.length) return null;
	return imgs.find((img) => img.isPrimary) ?? imgs[0];
}
